/**
 * TKT-2026-106839. Put the store's current offer on the trade-in lines the
 * buylist refused, so the customer can see it and confirm again.
 *
 * The buylist's price guard is the one authority on what a store pays. When it
 * refuses a cart it names each line that was over and what the store offers for
 * it (`storeOffer`), whatever the reason: a stale page, the quantity ladder, a
 * credit clamp. The kiosk takes that figure rather than re-deriving it, so the
 * resubmission is measured against exactly the number it was given.
 *
 * A line is matched on the identity the kiosk backend sends: productId,
 * condition, printing, language (with its NM / Normal / EN defaults), the
 * payment leg and, for a slab, its grader. A refusal from a buylist that predates line identity only names
 * the productId, so that is used only when it picks out exactly one line.
 *
 * A storeOffer of zero or less means the store is not buying that card at all,
 * so the line is removed rather than offered at nothing.
 *
 * On a line the store prices on a quantity ladder (TKT-2026-107051) the guard
 * measures the line against the engine's UN-REDUCED base, so that is what
 * storeOffer is. It becomes the ladder's base for the refused leg and the line
 * is walked again, which is the figure the buylist will book. Setting it as the
 * per-copy offer instead would show every copy at the base price while the
 * order booked the ladder.
 */

import { formatMoney } from './money';
import { buyLineCountOffsets, buyLineUnitOffers, hasTierData, resolveTierCondPrefix } from './buylistTierLadder';

const norm =(v, fallback) => {
    const s = v == null ? '' : String(v).trim();
    return (s === '' ? fallback : s).toUpperCase();
};

const lineIdentity = (item) => ({
    productId: String(item.productId),
    condAbbr: norm(item.variant?.condAbbr, 'NM'),
    printing: norm(item.variant?.printing, 'Normal'),
    langAbbr: norm(item.variant?.langAbbr, 'EN'),
    // A slab's violation names its grader, a raw line's names none
    // (buylistSubmitPriceGuard lineIdentity). TKT-2026-107322
    gradingCompany: norm(item.variant?.gradingCompany, ''),
});

const legOf = (item) => (item.paymentType === 'Credit' ? 'credit' : 'cash');

const matchesViolation = (item, v) => {
    if ((item.mode || 'sell') !== 'buy') return false;
    if (String(item.productId) !== String(v.productId)) return false;
    if (v.leg && legOf(item) !== v.leg) return false;
    if (!v.condAbbr) return true; // identity-less refusal, resolved below
    const id = lineIdentity(item);
    return (
        id.condAbbr === norm(v.condAbbr, 'NM') &&
        id.printing === norm(v.printing, 'Normal') &&
        id.langAbbr === norm(v.langAbbr, 'EN') &&
        id.gradingCompany === norm(v.gradingCompany, '')
    );
};

/** The line with the refused leg's offer replaced, on its ladder when it has one. */
const withStoreOffer = (item, offer) => {
    const credit = legOf(item) === 'credit';
    const ladder = item.buylistLadder;
    if (hasTierData(ladder)) {
        const key = `${resolveTierCondPrefix(item.variant?.condAbbr, ladder)}${credit ? 'FinalCredit' : 'FinalCash'}`;
        return { ...item, buylistLadder: { ...ladder, basePrices: { ...ladder.basePrices, [key]: offer } } };
    }
    return { ...item, [credit ? 'buylistCreditPrice' : 'buylistCashPrice']: offer };
};

/**
 * @param {Array} cart        the whole cart as stored (sell lines pass through untouched)
 * @param {Array} violations  `priceViolations` from the refusal
 * @param {string|null} [currency] the store currency, for the ladder walk
 * @returns {{ cart: Array, repriced: Array, removed: Array, unmatched: number }}
 */
export function applyBuylistPriceRefusal(cart, violations, currency = null) {
    const next = [...cart];
    const repriced = [];
    const removed = [];
    let unmatched = 0;
    const toRemove = new Set();

    for (const v of violations || []) {
        const hits = [];
        next.forEach((item, idx) => {
            if (!toRemove.has(idx) && matchesViolation(item, v)) hits.push(idx);
        });
        if (hits.length !== 1) {
            unmatched += 1;
            continue;
        }
        const idx = hits[0];
        const item = next[idx];
        const name = item.cleanName || item.name || v.name || 'card';
        const offer = Number(v.storeOffer);
        const leg = legOf(item);

        if (!Number.isFinite(offer) || offer <= 0) {
            toRemove.add(idx);
            removed.push({ name });
            continue;
        }
        // from / to are the per-copy figures the cart shows, before and after.
        const updated = withStoreOffer(item, offer);
        const alreadyInCart = buyLineCountOffsets(next)[idx];
        repriced.push({
            name,
            from: buyLineUnitOffers(item, currency, alreadyInCart)[leg],
            to: buyLineUnitOffers(updated, currency, alreadyInCart)[leg],
        });
        next[idx] = updated;
    }

    return {
        cart: next.filter((_, idx) => !toRemove.has(idx)),
        repriced,
        removed,
        unmatched,
    };
}

/**
 * The customer-facing sentence for what applyBuylistPriceRefusal did.
 * formatPrice is the store's money formatter (KioskThemeContext formatMoney).
 */
export function describeAppliedRefusal({ repriced, removed, unmatched }, formatPrice = (n) => formatMoney(n, null)) {
    const parts = [];
    if (repriced.length) {
        const list = repriced.map((r) => `${r.name} (now ${formatPrice(r.to)})`).join(', ');
        parts.push(`The store's current offer is lower on ${list}.`);
    }
    if (removed.length) {
        parts.push(`The store isn't buying ${removed.map((r) => r.name).join(', ')} right now, so ${removed.length === 1 ? 'it was' : 'they were'} removed.`);
    }
    if (unmatched) {
        parts.push('Some trade-in prices could not be updated here. Please ask a staff member for help.');
    }
    if (!repriced.length && !removed.length && !unmatched) {
        return 'Some trade-in prices have changed. Please review your cart and confirm again.';
    }
    if (repriced.length || removed.length) {
        parts.push('No order has been placed yet. Please review the new total and confirm again.');
    }
    return parts.join(' ');
}
